import React, {FC, useEffect, useState} from "react";
import GatewayService from "../../service/GatewayService";
import {ClientCombo} from "../../models/ClientCombo";
import {Pair} from "../../models/SGB";
import AppUtil from "../../util/AppUtil";
import AppConstants from "../../util/AppConstants";
import {DropDownCompString, getKeyVal} from "../util/DropDownComp";

interface MissedOppData {
    timestamp: number;
    clientCombo: ClientCombo;
}

const MissedOppComponent: FC = () => {
    const [missedOpps, setMissedOpps] = useState<MissedOppData[]>([]);
    const [sorter, setSorter] = useState(() => AppUtil.genericSorterFun("timestamp", (a: MissedOppData) => a.timestamp));
    const [isLoading, setIsLoading] = useState(true);
    const [minTime, setMinTime] = useState(0);
    const [maxTime, setMaxTime] = useState(1000);
    const [minProfit, setMinProfit] = useState(0);
    const [minEffectiveProfit, setMinEffectiveProfit] = useState(0);
    const [comboName, setComboName] = useState("");

    useEffect(() => {
        refresh();
    }, []);

    function refresh() {
        setIsLoading(true);
        GatewayService.getLastOpportunities()
            .then(data => {
                const formattedData: MissedOppData[] = data.map((item: Pair<number, ClientCombo>) => ({
                    timestamp: item.first,
                    clientCombo: {client : item.second.client, combo : AppUtil.fillIn(item.second.combo)}
                }));
                setMissedOpps(formattedData);
            })
            .catch(console.error)
            .finally(() => {
                setIsLoading(false);
            });
    }

    function sort(sortBy: string) {
        setSorter(() => AppUtil.genericSorterFun(sortBy, (a: MissedOppData) => {
            switch (sortBy) {
                case "timestamp":
                    return a.timestamp;
                case "client":
                    return a.clientCombo.client.charCodeAt(0); // Convert string to number for sorting
                case "capital":
                    return a.clientCombo.combo.capital;
                case "effectiveCapital":
                    return a.clientCombo.combo.effectiveCapital;
                case "time":
                    return a.clientCombo.combo.time;
                case "target":
                    return a.clientCombo.combo.target;
                case "effectiveProfit_qty":
                    return a.clientCombo.combo.effectiveProfit_qty;
                case "absProfit":
                    return a.clientCombo.combo.absProfit;
                case "maxSpread":
                    return a.clientCombo.combo.maxSpread;
                case "pointsDiff":
                    return a.clientCombo.combo.pointsDiff;
                case "ytm":
                    return a.clientCombo.combo.ytm;
                case "ytmWithoutMargin":
                    return a.clientCombo.combo.ytmWithoutMargin;
                case "margin":
                    return a.clientCombo.combo.margin;
                default:
                    return a.timestamp;
            }
        }));
    }

    const comboNames = getKeyVal(missedOpps.map(it => (it.clientCombo.combo as any).name || ''));

    const sortedData = missedOpps.filter(item => {
        const combo = item.clientCombo.combo as any;
        return (combo.time / AppConstants.DAY) >= minTime &&
            (combo.time / AppConstants.DAY) <= maxTime &&
            combo.target >= minProfit &&
            combo.effectiveProfit_qty >= minEffectiveProfit &&
            (comboName === "" || combo.name === comboName);
    }).sort(sorter);

    const totalEffectiveProfit = sortedData.reduce((sum, item) => sum + item.clientCombo.combo.effectiveProfit_qty, 0);

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading missed opportunities...</div>
            </div>
        );
    }

    if (missedOpps.length === 0) {
        return (
            <div className="missed-opp-controls">
                <button onClick={() => refresh()}>refresh</button>
                <span>No opportunities</span>
            </div>
        );
    }

    return <>
        <div className="missed-opp-controls">
            <button onClick={() => refresh()}>refresh</button>
            <span>Missed Opportunities ({sortedData.length}) | Total Effective Profit: {AppUtil.getRoundedOff(totalEffectiveProfit)}</span>
            <div>
                Min Time (days): <input type="number" value={minTime} onChange={e => setMinTime(Number(e.target.value))}/>
                Max Time (days): <input type="number" value={maxTime} onChange={e => setMaxTime(Number(e.target.value))}/>
                <DropDownCompString text={"Combo Name"} actual={comboName} arr={comboNames} set={setComboName}/>
                <br/>
                Min Profit (target): <input type="number" value={minProfit} onChange={e => setMinProfit(Number(e.target.value))}/>
                Min Effective Profit: <input type="number" value={minEffectiveProfit} onChange={e => setMinEffectiveProfit(Number(e.target.value))}/>
            </div>
        </div>
        
        <table className="full">
            <thead>
                <tr>
                    <th onClick={() => sort("timestamp")}>Time</th>
                    <th onClick={() => sort("client")}>Client</th>
                    <th>Type</th>
                    <th>Symbols</th>
                    <th onClick={() => sort("capital")}>Capital</th>
                    <th onClick={() => sort("effectiveCapital")}>Effective Capital</th>
                    <th onClick={() => sort("time")}>Time</th>
                    <th onClick={() => sort("target")}>Target</th>
                    <th onClick={() => sort("effectiveProfit_qty")}>Effective Profit</th>
                    <th onClick={() => sort("absProfit")}>Abs Profit</th>
                    <th>Max Spread Option</th>
                    <th onClick={() => sort("maxSpread")}>Max Spread</th>
                    <th onClick={() => sort("pointsDiff")}>Points Diff</th>
                    <th onClick={() => sort("ytm")}>YTM</th>
                    <th onClick={() => sort("ytmWithoutMargin")}>YTM WM</th>
                    <th onClick={() => sort("margin")}>Margin</th>
                    <th>Taxes</th>
                    <th>Taxes SqOff</th>
                </tr>
            </thead>
            <tbody>
                {sortedData.map((item, index) => (
                    <tr key={`${item.timestamp}-${index}`}>
                        <td>{AppUtil.formatTimeForDay(item.timestamp)}</td>
                        <td>{item.clientCombo.client}</td>
                        <td>{item.clientCombo.combo.type}</td>
                        <td className="symbols-cell">{item.clientCombo.combo.symbols}({item.clientCombo.combo.qty})</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.capital*item.clientCombo.combo._qty)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.effectiveCapital*item.clientCombo.combo._qty)}</td>
                        <td>{(item.clientCombo.combo.time / AppConstants.DAY).toFixed()}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.target)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.effectiveProfit_qty)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.absProfit)}</td>
                        <td>{item.clientCombo.combo.maxSpreadOption}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.maxSpread)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.pointsDiff)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.ytm)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.ytmWithoutMargin)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.margin*item.clientCombo.combo._qty)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.brokerage)}</td>
                        <td>{AppUtil.getRoundedOff(item.clientCombo.combo.brokerageSqOff)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </>;
};

export default MissedOppComponent; 
